import Close from '@mui/icons-material/Close';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AddOnRateType, SettlementLineItemType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { Fragment, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { AddOnCharge } from '~hooks/useAddOns/models';
import { Settlement } from '~hooks/useSettlements/models';
import { rateTypeTextByAddOnRateType } from '~pages/Settings/RatesManagement/AddOnCharges/addOnChargesDataGridColumnsDefinition';
import { useStores } from '~store';
import { formatPercentage } from '~utils/utilFunctions';

import { AddOnChargesDTO } from '../../AddOnChargesTableRows';
import { appendAddOnRateTypeToName, invoiceHelpers } from '../../helpers';
import { JobGroup, OrderGroup } from '../utils/groupLineItemsByOrderJob';
import { GridConfigType } from './constants';
import { QuickBooksItemCell, QuickBooksItemColumn } from './QuickBooksItemCell';
import { SettlementLineItems } from './SettlementLineItems';

interface GroupedSettlementLineItemsProps {
  orderGroups: OrderGroup[];
  /** Add-on charges not associated with any job (settlement-level add-ons) */
  ungroupedAddOnCharges?: AddOnCharge[];
  settlement?: Settlement;
  currencyFormatter: (value: number, options?: Intl.NumberFormatOptions) => string;
  gridConfig: GridConfigType;
  isRebill?: boolean;
  showNetColumns?: boolean;
  /** When true, render editable add-on charge rows */
  isEditing?: boolean;
  /** Set when the QuickBooks item column is shown */
  quickBooksItemColumn?: QuickBooksItemColumn;
}

interface OrderHeaderProps {
  orderGroup: OrderGroup;
}

function OrderHeader({ orderGroup }: OrderHeaderProps) {
  const theme = useTheme();
  const dispatchValue = orderGroup.dispatchNumberName ?? orderGroup.orderName;
  const showDispatch = !!dispatchValue;
  const showProject = !!orderGroup.projectName;
  const showOrderPo = !!orderGroup.poJobNumber;

  return (
    <Box
      sx={{
        gridColumn: '1 / -1',
        bgcolor: theme.semantic.surfaceSunken,
        px: 1,
        py: 1,
        borderTop: `2px solid ${theme.brandV2.colors.treadGray5}`,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
      }}
    >
      {showDispatch && (
        <Typography variant="body1">
          <Box
            component="span"
            sx={{
              fontWeight: 600,
            }}
          >
            {t('approvals.driver_pay.settlements.pdf.dispatch')}:
          </Box>{' '}
          {dispatchValue}
        </Typography>
      )}
      {showProject && (
        <Typography variant="body1">
          <Box
            component="span"
            sx={{
              fontWeight: 600,
            }}
          >
            {t('approvals.driver_pay.settlements.pdf.project')}:
          </Box>{' '}
          {orderGroup.projectName}
        </Typography>
      )}
      {showOrderPo && (
        <Typography variant="body1">
          <Box
            component="span"
            sx={{
              fontWeight: 600,
            }}
          >
            {t('order.form.order_po')}:
          </Box>{' '}
          {orderGroup.poJobNumber}
        </Typography>
      )}
      {orderGroup.orderOrderId && (
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
          }}
        >
          {orderGroup.orderOrderId}
        </Typography>
      )}
    </Box>
  );
}

type AddOnSelectOption = {
  id: string;
  name: string;
};

interface EditableAddOnChargeRowProps {
  formIndex: number;
  original?: AddOnCharge;
  settlement?: Settlement;
  currencyFormatter: (value: number) => string;
  gridConfig: GridConfigType;
  isRebill?: boolean;
  onRemove: (index: number) => void;
}

function EditableAddOnChargeRow({
  formIndex,
  original,
  settlement,
  currencyFormatter,
  gridConfig,
  isRebill = false,
  onRemove,
}: EditableAddOnChargeRowProps) {
  const theme = useTheme();
  const { addOnsStore } = useStores();
  const {
    control,
    formState: { errors },
  } = useFormContext<AddOnChargesDTO>();

  const watchedAddOnCharges = useWatch({ control, name: 'addOnCharges' });
  const editedAddOnCharge = watchedAddOnCharges?.[formIndex];

  const hasTicketNumbersColumn =
    settlement?.settlementLineItemType === SettlementLineItemType.LOAD;
  const rateTypeColumn = hasTicketNumbersColumn ? '10 / 11' : '8 / 9';

  const addOnSelectOptions = useMemo(() => {
    return addOnsStore.addOns.reduce((options, addOn) => {
      options.push({
        id: String(addOn.id),
        name: appendAddOnRateTypeToName(addOn.name, addOn.revenueCostRateType),
      });
      return options;
    }, [] as AddOnSelectOption[]);
  }, [addOnsStore.addOns]);

  if (!editedAddOnCharge || editedAddOnCharge._destroy === 1) {
    return null;
  }

  const isAddOnSelected = Boolean(editedAddOnCharge?.addOn?.id);
  const isNonRatePercentOfTotal =
    editedAddOnCharge?.addOnRateType === AddOnRateType.RATE_FOR_EACH ||
    editedAddOnCharge?.addOnRateType === AddOnRateType.PER_LOAD ||
    (editedAddOnCharge?.addOnRateType === AddOnRateType.FUEL_SURCHARGE &&
      !editedAddOnCharge?.isPercentage);

  const quantity = editedAddOnCharge?.quantity;
  const rate = editedAddOnCharge?.value;

  const dependsOnLineItems = invoiceHelpers.addOnTotalDependsOnLineItems(
    editedAddOnCharge?.addOnRateType as AddOnRateType | null,
  );
  const shouldRecompute =
    dependsOnLineItems ||
    invoiceHelpers.hasAddOnChargeEdits(editedAddOnCharge, original, isRebill);

  const total = isRebill
    ? shouldRecompute
      ? invoiceHelpers.calculateRebillAddOnChargeTotal(
          editedAddOnCharge?.addOnRateType
            ? (editedAddOnCharge.addOnRateType as AddOnRateType)
            : null,
          editedAddOnCharge?.quantity,
          editedAddOnCharge?.rebillValue,
          editedAddOnCharge?.isPercentage ? editedAddOnCharge?.rebillValue : 0,
          0,
          0,
        )
      : (original?.rebillTotalAmount ?? 0)
    : shouldRecompute
      ? invoiceHelpers.calculateAddOnChargeTotal(
          editedAddOnCharge?.addOnRateType
            ? (editedAddOnCharge.addOnRateType as AddOnRateType)
            : null,
          editedAddOnCharge?.quantity,
          editedAddOnCharge?.value,
          editedAddOnCharge?.isPercentage ? editedAddOnCharge?.value : 0,
          0,
          0,
        )
      : (original?.totalAmount ?? 0);

  return (
    <Box
      sx={{
        ...gridConfig,
        borderBottom: (theme) => `1px solid ${theme.semantic.border}`,
      }}
    >
      <AutocompleteFormField
        clearable
        control={control}
        errors={errors}
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        isRequired
        list={addOnSelectOptions}
        name={`addOnCharges[${formIndex}].addOn`}
        sx={{
          gridColumn: '1 / 4',
          fontSize: '12px',
          '& .MuiAutocomplete-inputRoot': { fontSize: '12px', width: '100%' },
          '& .MuiAutocomplete-endAdornment': {
            right: '5px !important',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 'fit-content',
          },
        }}
      />
      {isAddOnSelected && editedAddOnCharge?.addOnRateType && (
        <Typography variant="body2" sx={{ gridColumn: rateTypeColumn }}>
          {rateTypeTextByAddOnRateType[editedAddOnCharge.addOnRateType as AddOnRateType]}
        </Typography>
      )}
      {isAddOnSelected && isNonRatePercentOfTotal && (
        <TextFormField
          control={control}
          errors={errors}
          type="number"
          name={`addOnCharges[${formIndex}].quantity`}
          sx={{
            ml: 'auto',
            '& .MuiFormControl-root': { mb: 0 },
            '& .MuiOutlinedInput-root': { pr: 0 },
            '& .MuiOutlinedInput-input': { p: '5px 7px', fontSize: '12px' },
            gridArea: 'qty',
            '& .MuiInputBase-input': {
              fontSize: '12px',
            },
          }}
          inputProps={{ sx: { textAlign: 'right' } }}
        />
      )}
      {isAddOnSelected ? (
        <TextFormField
          control={control}
          errors={errors}
          type="number"
          name={`addOnCharges[${formIndex}].${isRebill ? 'rebillValue' : 'value'}`}
          sx={{
            ml: 'auto',
            '& .MuiFormControl-root': { mb: 0 },
            '& .MuiOutlinedInput-root': { pr: 0, pl: '7px' },
            '& .MuiOutlinedInput-input': {
              p: '5px 7px 5px 0px',
              fontSize: '12px',
            },
            gridArea: 'rate',
            '& .MuiInputBase-input': {
              fontSize: '12px',
            },
          }}
          inputProps={{ sx: { textAlign: 'right' } }}
          InputProps={{
            startAdornment: (
              <Typography
                sx={{
                  mr: '5px',
                  fontSize: '12px',
                }}
              >
                {isNonRatePercentOfTotal ? '$' : '%'}
              </Typography>
            ),
          }}
        />
      ) : null}
      <Box sx={{ gridArea: 'total', fontSize: '12px' }}>
        {quantity && rate && isNonRatePercentOfTotal
          ? total >= 0
            ? currencyFormatter(total)
            : `(${currencyFormatter(-total)})`
          : '$-'}
      </Box>
      <Tooltip title={t('actions.remove')}>
        <IconButton
          size="small"
          onClick={() => onRemove(formIndex)}
          sx={{ gridArea: 'action', justifySelf: 'end' }}
        >
          <Close fontSize="small" sx={{ color: theme.brandV2.colors.treadGray3 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

interface JobGroupAddOnChargesProps {
  addOnCharges: AddOnCharge[];
  settlement?: Settlement;
  currencyFormatter: (value: number) => string;
  gridConfig: GridConfigType;
  isRebill?: boolean;
  isEditing?: boolean;
  addOnChargeIndexMap?: Map<string, number>;
  onRemoveAddOnCharge?: (index: number) => void;
  quickBooksItemColumn?: QuickBooksItemColumn;
}

function JobGroupAddOnCharges({
  addOnCharges,
  settlement,
  currencyFormatter,
  gridConfig,
  isRebill = false,
  isEditing = false,
  addOnChargeIndexMap,
  onRemoveAddOnCharge,
  quickBooksItemColumn,
}: JobGroupAddOnChargesProps) {
  const hasTicketNumbersColumn =
    settlement?.settlementLineItemType === SettlementLineItemType.LOAD;
  const rateTypeColumn = hasTicketNumbersColumn ? '10 / 11' : '8 / 9';

  if (isEditing && addOnChargeIndexMap && onRemoveAddOnCharge) {
    return (
      <>
        {addOnCharges.map((addOnCharge) => {
          const formIndex = addOnChargeIndexMap.get(addOnCharge.id ?? '');
          if (formIndex === undefined) return null;

          return (
            <EditableAddOnChargeRow
              key={addOnCharge.id}
              formIndex={formIndex}
              original={addOnCharge}
              settlement={settlement}
              currencyFormatter={currencyFormatter}
              gridConfig={gridConfig}
              isRebill={isRebill}
              onRemove={onRemoveAddOnCharge}
            />
          );
        })}
      </>
    );
  }

  return (
    <>
      {addOnCharges.map((addOnCharge) => {
        const isPercentage =
          addOnCharge.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL ||
          (addOnCharge.revenueCostRateType === AddOnRateType.FUEL_SURCHARGE &&
            !!addOnCharge.percentage);

        return (
          <Box
            key={addOnCharge.id}
            sx={{
              ...gridConfig,
              borderBottom: (theme) => `1px solid ${theme.semantic.border}`,
            }}
          >
            <Typography variant="body2" sx={{ gridColumn: '1 / 4' }}>
              {`${t('approvals.driver_pay.settlements.add_on_name_prefixed', { name: addOnCharge.name })}`}
            </Typography>
            <Typography variant="body2" sx={{ gridColumn: rateTypeColumn }}>
              {rateTypeTextByAddOnRateType[addOnCharge.revenueCostRateType]}
            </Typography>
            {!!addOnCharge.quantity && (
              <Typography variant="body2" sx={{ gridArea: 'qty' }}>
                {addOnCharge.quantity}
              </Typography>
            )}

            {isPercentage && (
              <Typography variant="body2" sx={{ gridArea: 'rate' }}>
                {(() => {
                  const percentage =
                    isRebill && !isNil(addOnCharge.rebillPercentage)
                      ? addOnCharge.rebillPercentage
                      : addOnCharge.percentage;

                  return formatPercentage(percentage);
                })()}
              </Typography>
            )}

            {!isPercentage && (
              <Typography variant="body2" sx={{ gridArea: 'rate' }}>
                {(() => {
                  const rate =
                    isRebill && !isNil(addOnCharge.rebillRevenueRateValue)
                      ? addOnCharge.rebillRevenueRateValue
                      : (addOnCharge.rate ?? 0);

                  return rate >= 0
                    ? currencyFormatter(rate)
                    : `(${currencyFormatter(-rate)})`;
                })()}
              </Typography>
            )}

            <Typography variant="body2" sx={{ gridArea: 'total' }}>
              {(() => {
                const total =
                  isRebill && !isNil(addOnCharge.rebillTotalAmount)
                    ? addOnCharge.rebillTotalAmount
                    : addOnCharge.totalAmount;

                return total >= 0
                  ? currencyFormatter(total)
                  : `(${currencyFormatter(-total)})`;
              })()}
            </Typography>
            {quickBooksItemColumn && (
              <Box sx={{ gridArea: 'qb' }}>
                <QuickBooksItemCell
                  areMatchesLoaded={quickBooksItemColumn.areMatchesLoaded}
                  isMatching={quickBooksItemColumn.isMatching}
                  isReadOnly={quickBooksItemColumn.isReadOnly}
                  itemOptions={quickBooksItemColumn.itemOptions}
                  lines={addOnCharge.id ? [{ addOnChargeId: addOnCharge.id }] : []}
                  matches={
                    quickBooksItemColumn.matchesByAddOnChargeId.get(
                      addOnCharge.id ?? '',
                    ) ?? []
                  }
                  onRunMatching={quickBooksItemColumn.onRunMatching}
                  onSelect={quickBooksItemColumn.onSelect}
                />
              </Box>
            )}
          </Box>
        );
      })}
    </>
  );
}

interface JobGroupRendererProps {
  jobGroup: JobGroup;
  settlement?: Settlement;
  currencyFormatter: (value: number, options?: Intl.NumberFormatOptions) => string;
  gridConfig: GridConfigType;
  isRebill?: boolean;
  showNetColumns?: boolean;
  isFirstJob?: boolean;
  quickBooksItemColumn?: QuickBooksItemColumn;
}

function JobGroupRenderer({
  jobGroup,
  settlement,
  currencyFormatter,
  gridConfig,
  isRebill = false,
  showNetColumns = false,
  isFirstJob = false,
  quickBooksItemColumn,
}: JobGroupRendererProps) {
  const theme = useTheme();
  // Create the isFirstInJobGroup array - first item is true, rest are false
  const isFirstInJobGroup = useMemo(() => {
    return jobGroup.lineItems.map((_, index) => index === 0);
  }, [jobGroup.lineItems]);

  return (
    <Box
      sx={{
        gridColumn: '1 / -1',
        borderTop: isFirstJob ? 'none' : `1px solid ${theme.brandV2.colors.treadGray6}`,
      }}
    >
      <SettlementLineItems
        lineItems={jobGroup.lineItems}
        settlement={settlement}
        currencyFormatter={currencyFormatter}
        gridConfig={gridConfig}
        isRebill={isRebill}
        showNetColumns={showNetColumns}
        isGroupedView={true}
        isFirstInJobGroup={isFirstInJobGroup}
        quickBooksItemColumn={quickBooksItemColumn}
      />
      {jobGroup.addOnCharges.length > 0 && (
        <JobGroupAddOnCharges
          addOnCharges={jobGroup.addOnCharges}
          settlement={settlement}
          currencyFormatter={currencyFormatter}
          gridConfig={gridConfig}
          isRebill={isRebill}
          quickBooksItemColumn={quickBooksItemColumn}
        />
      )}
    </Box>
  );
}

function SettlementAddOnsHeader() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        gridColumn: '1 / -1',
        bgcolor: theme.semantic.surfaceSunken,
        px: 1,
        py: 1,
        borderTop: `2px solid ${theme.brandV2.colors.treadGray5}`,
        mt: 2,
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
        }}
      >
        {t('approvals.driver_pay.settlements.settlement_add_ons')}
      </Typography>
    </Box>
  );
}

export function GroupedSettlementLineItems({
  orderGroups,
  ungroupedAddOnCharges = [],
  settlement,
  currencyFormatter,
  gridConfig,
  isRebill = false,
  showNetColumns = false,
  isEditing = false,
  quickBooksItemColumn,
}: GroupedSettlementLineItemsProps) {
  return (
    <>
      {orderGroups.map((orderGroup) => (
        <Fragment key={orderGroup.orderId}>
          <OrderHeader orderGroup={orderGroup} />
          {orderGroup.jobs.map((jobGroup, jobIndex) => (
            <JobGroupRenderer
              key={jobGroup.jobId}
              jobGroup={jobGroup}
              settlement={settlement}
              currencyFormatter={currencyFormatter}
              gridConfig={gridConfig}
              isRebill={isRebill}
              showNetColumns={showNetColumns}
              isFirstJob={jobIndex === 0}
              quickBooksItemColumn={quickBooksItemColumn}
            />
          ))}
        </Fragment>
      ))}

      {/* Settlement-level add-ons (not associated with any job)
          In edit mode, this section is handled by SettlementAddOnCharges component */}
      {!isEditing && ungroupedAddOnCharges.length > 0 && (
        <>
          <SettlementAddOnsHeader />
          <JobGroupAddOnCharges
            addOnCharges={ungroupedAddOnCharges}
            settlement={settlement}
            currencyFormatter={currencyFormatter}
            gridConfig={gridConfig}
            isRebill={isRebill}
            quickBooksItemColumn={quickBooksItemColumn}
          />
        </>
      )}
    </>
  );
}
